interface VideoModalElements {
    videoTitle: string;
    videoGUID: string;
    vidBuild: string;
}

class VideoModalHandler {
    private static readonly VIMEO_BASE_URL = 'https://vimeo.com/';
    private static readonly YOUTUBE_BASE_URL = 'https://www.youtube.com/watch?v=';
    private static readonly VIMEO_PLAYER_URL = 'https://player.vimeo.com/video/';
    private static readonly YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/';

    private static extractVideoTitle(target: HTMLElement): string {
        const hiddenSpan = target.querySelector<HTMLSpanElement>('span.visuallyhidden');
        return hiddenSpan?.innerText || '';
    }

    private static extractVideoId(url: string, baseUrl: string): string {
        let vid = url.replace(baseUrl, '');
        const slashIndex = vid.indexOf('/');
        if (slashIndex !== -1) {
            vid = vid.substring(0, slashIndex);
        }
        return vid;
    }

    private static buildVimeoEmbed(videoId: string, videoTitle: string): string {
        return `
            <div style='padding: 56.25% 0 0 0; position: relative;'>
                <iframe 
                    src='${this.VIMEO_PLAYER_URL}${videoId}?title=0&byline=0&portrait=0' 
                    alt='${videoTitle}' 
                    style='position:absolute;top:0;left:0;width:100%;height:100%;' 
                    frameborder='0' 
                    allow='autoplay; fullscreen' 
                    allowfullscreen>
                </iframe>
            </div>
            <script src='https://player.vimeo.com/api/player.js'></script>
            <div class='transcript-container js-accordion'>
                <button class='transcript-button js-accordion-button' type='button' aria-expanded='false'>
                    Video Transcript 
                    <img src='/Content/images/arrow-down.svg' width='19' height='10' alt='' />
                </button>
                <div class='transcript-content js-accordion-content'>
                    <p>This video uses Vimeo's native closed captioning features to provide a transcript. 
                    If closed captioning is unavailable for this video, please <a href='/contact'>contact us</a> 
                    and we can provide you with one.</p>
                </div>
            </div>
        `;
    }

    private static buildYouTubeEmbed(videoId: string, videoTitle: string): string {
        return `
            <div style='padding: 56.25% 0 0 0; position: relative;'>
                <iframe 
                    src='${this.YOUTUBE_EMBED_URL}${videoId}' 
                    alt='${videoTitle}' 
                    style='position:absolute;top:0;left:0;width:100%;height:100%;' 
                    frameborder='0' 
                    allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen' 
                    allowfullscreen>
                </iframe>
            </div>
            <div class='transcript-container js-accordion'>
                <button class='transcript-button js-accordion-button' type='button' aria-expanded='false'>
                    Video Transcript 
                    <img src='/Content/images/arrow-down.svg' width='19' height='10' alt='' />
                </button>
                <div class='transcript-content js-accordion-content'>
                    <p>This video uses YouTube's native closed captioning features to provide a transcript. 
                    If closed captioning is unavailable for this video, please <a href='/contact'>contact us</a> 
                    and we can provide you with one.</p>
                </div>
            </div>
        `;
    }

    private static buildMP4Embed(videoUrl: string, videoGUID: string): string {
        return `
            <video width='100%' controls autoplay>
                <source src='${videoUrl}' type='video/mp4'>
            </video>
            <div class='transcript-container js-accordion'>
                <button class='transcript-button js-accordion-button' type='button' aria-expanded='false'>
                    Video Transcript 
                    <img src='/Content/images/arrow-down.svg' width='19' height='10' alt='' />
                </button>
                <div id='transcript-${videoGUID}' class='transcript-content js-accordion-content'>
                    <p>For a transcript of this video, please <a href='/contact'>contact us</a>.</p>
                </div>
            </div>
        `;
    }

    private static async fetchTranscript(videoGUID: string): Promise<void> {
        const transcriptElement = document.getElementById(`transcript-${videoGUID}`);
        if (!transcriptElement) return;

        try {
            const response = await fetch(`/transcript/${videoGUID}`);
            if (response.ok) {
                const transcriptText = await response.text();
                const cleanedText = transcriptText.replace(/"/g, '');
                if (cleanedText) {
                    transcriptElement.innerHTML = `<p>${cleanedText.replace(/\\r\\n/g, '<br />')}</p>`;
                }
            }
        } catch (error) {
            console.error('Transcript retrieval error:', error);
        }
    }

    private static processVideoModal(target: HTMLElement): void {
        const videoUrl = target.getAttribute('data-video');
        if (!videoUrl) return;

        const videoTitle = this.extractVideoTitle(target);
        let videoGUID = '';
        let videoContent = '';

        if (videoUrl.includes('vimeo')) {
            const videoId = this.extractVideoId(videoUrl, this.VIMEO_BASE_URL);
            videoContent = this.buildVimeoEmbed(videoId, videoTitle);
        } else if (videoUrl.includes('youtube')) {
            const videoId = this.extractVideoId(videoUrl, this.YOUTUBE_BASE_URL);
            videoContent = this.buildYouTubeEmbed(videoId, videoTitle);
        } else if (videoUrl.includes('.mp4')) {
            videoGUID = videoUrl.split('/')[2];
            videoContent = this.buildMP4Embed(videoUrl, videoGUID);
        }

        const modalContent = `
            <div class='video-modal-content'>
                <button class='video-modal-close'>
                    <span class='visuallyhidden'> close</span>
                </button>
                ${videoContent}
            </div>
        `;

        this.removeExistingModal();
        this.createModal(modalContent);

        if (videoGUID) {
            this.fetchTranscript(videoGUID);
        }
    }

    private static removeExistingModal(): void {
        const existingModal = document.querySelector('.video-modal');
        existingModal?.remove();
    }

    private static createModal(content: string): void {
        const modalDiv = document.createElement('div');
        modalDiv.classList.add('video-modal');
        modalDiv.innerHTML = content;
        document.body.appendChild(modalDiv);
    }

    private static closeModal(): void {
        const modal = document.querySelector('.video-modal');
        modal?.remove();
    }

    private static isVideoTrigger(target: HTMLElement): boolean {
        return target.matches('[data-video]') && 
               !target.parentElement?.classList.contains('cta-buttons') &&
               target.parentElement?.id !== 'ctaButtons';
    }

    public static handleClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        if (this.isVideoTrigger(target)) {
            const videoUrl = target.getAttribute('data-video');
            if (videoUrl) {
                event.preventDefault();
                this.processVideoModal(target);
            }
        } else if (target.matches('.video-modal')) {
            this.closeModal();
        } else if (target.matches('.video-modal-content')) {
            event.stopPropagation();
        } else if (target.matches('.video-modal-close')) {
            event.preventDefault();
            this.closeModal();
        }
    }
}

// Event listener setup
document.addEventListener('click', (event: MouseEvent) => {
    VideoModalHandler.handleClick(event);
});